import type { Schemas } from '@kanos/api-client';
import { clientKindLabel } from '@kanos/domain';
import { Button } from '@kanos/ui';
import { api, failure, formatDate, meQuery, ProblemAlert, startSignIn } from '@kanos/web-core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CircleCheck, MailOpen, Smartphone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useBranding } from './branding';
import { takeLinkToken } from './link-token';
import { Lead, PageLoading, PortalPage } from './portal-page';

type InvitationOpened = Schemas['InvitationOpened'];
type RespondentIntake = Schemas['RespondentIntake'];

const HERE = '/invitation';

/** Opens the link: the API answers who invites and keeps the link for after the sign-in. */
function useOpenedInvitation(token: string | null) {
  return useQuery({
    queryKey: ['portal', 'invitation', token],
    enabled: token !== null,
    retry: false,
    staleTime: Infinity,
    queryFn: async (): Promise<InvitationOpened> => {
      const { data, error, response } = await api.POST('/api/v1/portal/invitation', {
        body: { token: token ?? '' },
      });
      if (!data) throw failure('openInvitation', error, response.status);
      return data;
    },
  });
}

function useAcceptInvitation() {
  return useMutation({
    mutationFn: async (): Promise<RespondentIntake> => {
      const { data, error, response } = await api.POST('/api/v1/portal/invitation/accept');
      if (!data) throw failure('acceptInvitation', error, response.status);
      return data;
    },
  });
}

/**
 * The invitation of a firm into its portal (ADR-026): the link from the e-mail opens it; the
 * person signs up - or in - and comes back here, where the invitation is accepted with that
 * account. The second factor is set up on the way, at Keycloak.
 */
export function InvitationPage() {
  const branding = useBranding();
  const [token] = useState(() => takeLinkToken(HERE));
  const me = useQuery(meQuery);
  const opened = useOpenedInvitation(token);
  const accept = useAcceptInvitation();
  const signedIn = me.data?.app === 'client';

  // Back from the sign-in: the link waits in its cookie - accepted at once.
  useEffect(() => {
    if (token === null && signedIn && accept.isIdle) accept.mutate();
  }, [token, signedIn, accept]);

  const content = () => {
    if (me.isPending || (token !== null && opened.isPending)) return <PageLoading />;
    if (accept.isSuccess) return <Accepted intake={accept.data} firmName={branding?.firmName} />;
    if (accept.isError) return <ProblemAlert error={accept.error} />;
    if (accept.isPending) return <PageLoading />;
    if (opened.isError) return <ProblemAlert error={opened.error} />;
    if (opened.data) {
      return (
        <Invited
          invitation={opened.data}
          signedIn={signedIn}
          isAccepting={accept.isPending}
          onAccept={() => accept.mutate()}
        />
      );
    }
    return (
      <Lead title="Ihre Einladung">
        Bitte öffnen Sie den Link aus der E-Mail Ihrer Kanzlei erneut – er bringt Sie hierher
        zurück.
      </Lead>
    );
  };

  return <PortalPage branding={branding}>{content()}</PortalPage>;
}

function Invited({
  invitation,
  signedIn,
  isAccepting,
  onAccept,
}: {
  invitation: InvitationOpened;
  signedIn: boolean;
  isAccepting: boolean;
  onAccept: () => void;
}) {
  return (
    <section className="grid gap-6">
      <MailOpen aria-hidden className="size-10 text-accent-text" />
      <Lead title={`Einladung von ${invitation.firmName}`}>
        Die Kanzlei bittet Sie um Angaben für Ihre Aufnahme als Mandant. Dafür legen Sie ein Konto
        mit Ihrer E-Mail-Adresse {invitation.email} an. Die Einladung gilt bis{' '}
        {formatDate(invitation.expiresAt)}.
      </Lead>
      {!signedIn && <SecondFactorHelp firmName={invitation.firmName} />}
      {signedIn ? (
        <Button className="justify-self-start" isPending={isAccepting} onPress={onAccept}>
          Einladung annehmen
        </Button>
      ) : (
        <div className="flex flex-col gap-3 sm:flex-row">
          <Button onPress={() => startSignIn(HERE, 'signup')}>Konto anlegen</Button>
          <Button variant="secondary" onPress={() => startSignIn(HERE)}>
            Ich habe schon ein Konto
          </Button>
        </div>
      )}
    </section>
  );
}

/**
 * The second factor, said calmly before the person meets it at the sign-up: why, what it needs,
 * and whom to ask when it does not work - its firm, never a dead end.
 */
function SecondFactorHelp({ firmName }: { firmName: string }) {
  return (
    <div className="glass grid gap-3 rounded-lg p-5">
      <h2 className="flex items-center gap-2 text-base font-semibold">
        <Smartphone aria-hidden className="size-5 text-accent-text" />
        Anmeldung mit Ihrem Smartphone
      </h2>
      <p className="text-sm">
        Zum Schutz Ihrer Daten bestätigen Sie die Anmeldung zusätzlich mit Ihrem Smartphone. Sie
        brauchen dafür eine kostenlose App, z. B. Google Authenticator oder Microsoft Authenticator
        – wir führen Sie Schritt für Schritt.
      </p>
      <ol className="grid list-decimal gap-1 pl-5 text-sm text-muted">
        <li>Installieren Sie die App aus dem App Store oder von Google Play.</li>
        <li>Legen Sie hier Ihr Konto an; danach sehen Sie einen QR-Code.</li>
        <li>Scannen Sie den Code mit der App und geben Sie die angezeigte Zahl ein.</li>
      </ol>
      <p className="text-sm text-muted">
        Klappt etwas nicht, ist das kein Problem: Wenden Sie sich an {firmName}, die Kanzlei hilft
        Ihnen weiter.
      </p>
    </div>
  );
}

function Accepted({
  intake,
  firmName,
}: {
  intake: RespondentIntake;
  firmName: string | null | undefined;
}) {
  return (
    <section aria-live="polite" className="glass grid gap-4 rounded-lg p-6 sm:p-8">
      <CircleCheck aria-hidden className="size-10 text-success-fg" />
      <h1 className="text-3xl font-semibold tracking-[-0.03em]">Einladung angenommen.</h1>
      <p className="text-muted sm:text-lg">
        Ihr Konto gehört jetzt zur Aufnahme „{intake.name}“ ({clientKindLabel[intake.kind]}) bei{' '}
        {firmName ?? 'der Kanzlei'}. Als Nächstes machen Sie Ihre Angaben.
      </p>
      <Button className="justify-self-start" onPress={() => window.location.assign('/intake')}>
        Zu Ihren Angaben
      </Button>
    </section>
  );
}
